import type { Scene } from '@babylonjs/core/scene';
import type { DirectionalLight } from '@babylonjs/core/Lights/directionalLight';
import { PointLight } from '@babylonjs/core/Lights/pointLight';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import type { Material } from '@babylonjs/core/Materials/material';
import type { RenderingConfig } from '../config/rendering.config';
import type { ZonesConfig } from '../config/zones.config';
import { forwardOf } from '../level/connectors/Pose';
import type { EnclosureKind } from '../level/segments/SegmentDefinition';
import {
  blendLighting,
  copyLighting,
  hexToRgb,
  lightingChanged,
  lightingState,
  skylineDayTint,
  stepTransition,
  type DayLighting,
  type LightingState,
} from '../zones/lightingBlend';
import { smoothstep } from '../utils/math';
import type { RenderingEnvironment } from './RenderingEnvironment';

export interface ZoneLightingInput {
  /** Zone at the runner's position (the blend follows over `transitionSeconds`). */
  readonly night: boolean;
  /** Enclosed stretch around the camera and how far inside it is (0..1). */
  readonly enclosure: EnclosureKind | null;
  readonly enclosureFactor: number;
  /** Anchor of the runner light (track centre at the runner's distance, feet height) and the track heading. */
  readonly runner: { readonly x: number; readonly y: number; readonly z: number; readonly yaw: number };
}

/** Frames a blend must stay unchanged before thawed materials are frozen again. */
const REFREEZE_AFTER_FRAMES = 3;

/**
 * Day/night zones and enclosed stretches (Phase 3) on screen: blends sun, image based lighting,
 * sky dome, skyline, fog and a runner light between the day setup of RenderingConfig and the
 * night/enclosure profiles of ZonesConfig. Emissive materials (lamps, lenses, banners) keep
 * glowing, so they read as light sources at night.
 *
 * Frozen materials (greybox library) only take new IBL intensities while unfrozen – they are
 * thawed while the light changes and frozen again once it is steady.
 */
export class ZoneLighting {
  private readonly day: DayLighting;
  private readonly runnerLight: PointLight;
  private readonly runnerColor: Color3;
  private readonly target: LightingState = lightingState();
  private readonly applied: LightingState = lightingState();
  private nightProgress = 0;
  private hasApplied = false;
  private steadyFrames = 0;
  private readonly thawed: Material[] = [];
  private readonly color = new Color3();

  constructor(
    private readonly scene: Scene,
    private readonly sun: DirectionalLight,
    private readonly environment: RenderingEnvironment,
    rendering: RenderingConfig,
    private readonly config: ZonesConfig,
  ) {
    this.day = {
      sunIntensity: rendering.sunIntensity,
      sunColor: hexToRgb(rendering.sunColor),
      environmentIntensity: rendering.environment.enabled ? rendering.environment.intensity : scene.environmentIntensity,
      fogColor: hexToRgb(rendering.fog.color),
      fogStart: rendering.fog.start,
      fogEnd: rendering.fog.end,
      skylineTint: rendering.environment.skyline
        ? skylineDayTint(hexToRgb(rendering.fog.color), hexToRgb(rendering.environment.skyline.paintedHaze))
        : [1, 1, 1],
    };
    const lamp = config.runnerLight;
    this.runnerColor = Color3.FromHexString(lamp.color);
    this.runnerLight = new PointLight('LIGHT_RUNNER', Vector3.Zero(), scene);
    this.runnerLight.diffuse = this.runnerColor.clone();
    this.runnerLight.specular = this.runnerColor.scale(0.3);
    this.runnerLight.range = lamp.range;
    // Always on (intensity 0 by day) – enabling a light later would recompile every material.
    this.runnerLight.intensity = 0;
    const dome = environment.sky.dome;
    if (dome) {
      // Tintable sky: the dome image is multiplied by primaryColor (white by day = unchanged).
      dome.material.useRGBColor = false;
      dome.material.primaryColor = Color3.White();
    }
  }

  /** New run: starts in the given zone without a blend. */
  reset(night = false): void {
    this.nightProgress = night ? 1 : 0;
    this.hasApplied = false;
  }

  /** Current night share 0..1 (eased) – for debug and tests in the browser. */
  get nightFactor(): number {
    return smoothstep(this.nightProgress);
  }

  /** Share of the day sun applied now (night, enclosures dim it) – 1 = open day or nothing applied yet. */
  get daylight(): number {
    if (!this.hasApplied || this.day.sunIntensity <= 0) return 1;
    return this.applied.sunIntensity / this.day.sunIntensity;
  }

  update(dt: number, input: ZoneLightingInput): void {
    if (!this.config.enabled) return;
    this.nightProgress = stepTransition(this.nightProgress, input.night, dt, this.config.transitionSeconds);
    const enclosure = input.enclosure ? this.config.enclosures[input.enclosure] : null;
    blendLighting(this.day, this.config.night, this.nightFactor, enclosure, input.enclosureFactor, this.target);
    this.placeRunnerLight(input.runner);

    if (this.hasApplied && !lightingChanged(this.target, this.applied)) {
      if (this.thawed.length > 0 && ++this.steadyFrames >= REFREEZE_AFTER_FRAMES) this.refreeze();
      return;
    }
    this.steadyFrames = 0;
    this.thaw();
    this.apply(this.target);
    copyLighting(this.target, this.applied);
    this.hasApplied = true;
  }

  private placeRunnerLight(runner: ZoneLightingInput['runner']): void {
    const lamp = this.config.runnerLight;
    const ahead = forwardOf(runner.yaw);
    this.runnerLight.position.set(runner.x + ahead.x * lamp.ahead, runner.y + lamp.height, runner.z + ahead.z * lamp.ahead);
  }

  private apply(state: LightingState): void {
    this.sun.intensity = state.sunIntensity;
    this.sun.diffuse.set(...state.sunColor);
    this.sun.specular.copyFrom(this.sun.diffuse);
    this.scene.environmentIntensity = state.environmentIntensity;
    this.runnerLight.intensity = this.config.runnerLight.intensity * state.runnerLight;

    this.scene.fogColor.set(...state.fogColor);
    this.scene.fogStart = state.fogStart;
    this.scene.fogEnd = state.fogEnd;
    this.scene.clearColor.set(state.fogColor[0], state.fogColor[1], state.fogColor[2], 1);

    const { dome, skyline } = this.environment.sky;
    if (dome) {
      dome.texture.level = state.skyLevel;
      dome.material.primaryColor.set(...state.skyTint);
    }
    if (skyline) {
      this.color.set(...state.skylineTint);
      skyline.emissiveColor.copyFrom(this.color.scaleInPlace(state.skylineLevel));
    }
  }

  /** Frozen materials skip uniform updates – unfreeze them while the light changes. */
  private thaw(): void {
    for (const material of this.scene.materials) {
      if (!material.isFrozen) continue;
      material.unfreeze();
      this.thawed.push(material);
    }
  }

  private refreeze(): void {
    for (const material of this.thawed) material.freeze();
    this.thawed.length = 0;
    this.steadyFrames = 0;
  }
}
